import {SurfaceBasedTrackingEvents} from "@buildwithflux/models";
// eslint-disable-next-line no-restricted-imports
import {CloseReason, SnackbarKey, SnackbarMessage, useSnackbar} from "notistack";
import React, {useCallback} from "react";

import {useFluxServices} from "injection/hooks";
import {INotificationOptions} from "modules/app/state/app";

function reactNodeToString(node: React.ReactNode): string {
    if (typeof node === "string") {
        return node;
    } else if (typeof node === "number") {
        return node.toString();
    } else if (Array.isArray(node)) {
        return node.map(reactNodeToString).join("");
    } else {
        return "";
    }
}

export function useFluxSnackbar() {
    const snackbar = useSnackbar();
    const {analyticsStorage} = useFluxServices();

    const enqueueSnackbar = useCallback(
        (
            message: SnackbarMessage,
            // Provide the surface or object that is firing the snackbar.
            // Ideally re-use existing values
            surfaceOrObject: "project" | "component" | "part" | "asset" | string,
            options?: INotificationOptions,
        ) => {
            const key = snackbar.enqueueSnackbar(message, options);

            analyticsStorage.logEvent(SurfaceBasedTrackingEvents.snackbar, {
                action: "enqueueSnackbar",
                key: key,
                surfaceOrObject: surfaceOrObject,
                variant: options?.variant,
                content: reactNodeToString(message),
            });

            if (options) {
                const originalOnClose = options.onClose;

                options.onClose = (
                    event: React.SyntheticEvent<unknown> | null,
                    reason: CloseReason,
                    key?: SnackbarKey,
                ) => {
                    // Call the original onClose if it exists
                    if (originalOnClose) {
                        originalOnClose(event, reason, key);
                    }

                    analyticsStorage.logEvent(SurfaceBasedTrackingEvents.snackbar, {
                        action: "onClose",
                        key,
                        surfaceOrObject,
                        reason,
                        variant: options?.variant,
                        position: `${options?.anchorOrigin?.vertical}-${options?.anchorOrigin?.horizontal}`,
                        content: reactNodeToString(message),
                    });
                };
            }

            return key;
        },
        [snackbar, analyticsStorage],
    );

    const closeSnackbar = useCallback(
        (key: SnackbarKey) => {
            snackbar.closeSnackbar(key);

            analyticsStorage.logEvent(SurfaceBasedTrackingEvents.snackbar, {
                action: "closeSnackbar",
                key: key,
            });
        },
        [snackbar, analyticsStorage],
    );

    return {enqueueSnackbar, closeSnackbar};
}
